import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, CheckCircle2, Circle } from 'lucide-angular';
import { GradingBadgeService } from '../../services/grading-badge.service';
import { take } from 'rxjs';
import { AmplitudeService } from '../../../../../common/analytics/amplitude.service';

/**
 * Badge component that displays grading status (active/inactive)
 * Shows inactive state until grading is performed, then switches to active
 * Clicking the badge scrolls to grading results or shows a popover if not graded
 */
@Component({
  selector: 'app-grading-badge',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './grading-badge.component.html',
  styleUrl: './grading-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GradingBadgeComponent {
  private readonly badgeService = inject(GradingBadgeService);
  private readonly amplitudeService = inject(AmplitudeService);

  protected readonly isActive$ = this.badgeService.isActive$;
  protected readonly showPopover = signal(false);

  protected readonly icons = {
    CheckCircle2,
    Circle,
  };

  private popoverTimeoutId: ReturnType<typeof setTimeout> | null = null;

  protected onBadgeClick(): void {
    // Check if grading is active
    this.badgeService.isActive$.pipe(take(1)).subscribe((isActive) => {
      // Track badge click
      this.amplitudeService.trackEvent('grading_badge_clicked', {
        isGraded: isActive,
      });

      if (isActive) {
        // Scroll to grading results
        this.badgeService.scrollToGrading();
      } else {
        // Show popover
        this.showPopoverMessage();
      }
    });
  }

  private showPopoverMessage(): void {
    // Clear any existing timeout
    if (this.popoverTimeoutId) {
      clearTimeout(this.popoverTimeoutId);
    }

    // Show the popover
    this.showPopover.set(true);

    // Auto-hide after 3 seconds
    this.popoverTimeoutId = setTimeout(() => {
      this.showPopover.set(false);
      this.popoverTimeoutId = null;
    }, 3000);
  }

  protected hidePopover(): void {
    this.showPopover.set(false);
    if (this.popoverTimeoutId) {
      clearTimeout(this.popoverTimeoutId);
      this.popoverTimeoutId = null;
    }
  }
}
